import {
	CaptchaForm,
	Checkbox,
	FieldValidationProvider,
	FormValidationProvider,
	Heading,
	TextField,
	getCaptchaSolution,
} from '@/atoms';
import { getEditableFields, getFieldType } from '@/bloks/Form/ZendeskForm/utils';
import { useServerPageConfig } from '@/contexts/config';
import { ZendeskCustomField, ZendeskHandlerResponse } from '@/types';
import { fetchApi, slugifyName } from '@/utils';
import { useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import styles from './EducationFeedbackForm.module.scss';

interface Props {
	onCancel?: (() => void) | null;
	onSubmit?: (() => void) | null;
	vimeoId: string;
}

export const EducationFeedbackForm: React.FC<Props> = ({ vimeoId, onCancel = null }) => {
	const config = useServerPageConfig();
	const isDev = config.env === 'dev';
	const locale = config.locale ?? 'sv';
	const formId = isDev ? '17818260642450' : '27350186024466';
	const groupId = isDev ? '360002525120' : '24759075';
	const formRef = useRef<HTMLDivElement | null>(null);
	const [fetchedForm, setFetchedForm] = useState<ZendeskHandlerResponse | null>(null);
	const [feedbackData, setFeedbackData] = useState<{
		description: string | null;
		reason: { id: number; value: { id: number; name: string; value: string }[] };
	}>({
		description: null,
		reason: { id: -1, value: [] },
	});

	useEffect(() => {
		if (!fetchedForm) {
			getForm(formId, locale);
			return;
		}
	}, [fetchedForm, formId, locale]);

	const getForm = async (formId: string, locale: string) => {
		const res = await fetchApi(`zendesk-v1/[...formId] GET`, formId, locale);
		if (res.body?.data) {
			setFetchedForm(res.body.data);
		}
	};

	const handleOnSubmit = (e: React.BaseSyntheticEvent) => {
		const formElement = formRef.current?.querySelector(`#ZENDESK-education-feedback`) as unknown as HTMLFormElement;
		const formData = new FormData(formElement);
		const customFields: ZendeskCustomField[] = [];
		formData.forEach((v, k) => {
			const prop = Number(k);
			if (!isNaN(prop)) {
				customFields.push({ id: prop, value: v.toString() });
			}
		});
		return async function postFeedbackForm() {
			const description = formData.get('description') as string;
			const data = {
				customFormFields: customFields,
				groupId,
				priority: 'medium',
				subject: formData.get('subject')?.toString() ?? '',
				formId,
				formTag: 'inkommit_via_hemsidan_feedback',
				description: `${Array.isArray(feedbackData.reason.value) && feedbackData.reason.value.length ? `Reasons: ${feedbackData.reason.value.map((v) => v.name).join(', ')}\n` : ''}Description/Comment: ${description ?? 'Ingen kommentar'}\n`,
			};
			const body = {
				data,
				captchaSolution: getCaptchaSolution(e),
			};

			await fetchApi('zendesk-v1/[...formId] POST', body);
		};
	};

	const handleSelectInput = (value: string, optionId: number, fieldId: number, name: string) => {
		if (feedbackData.reason.value.some((selectedValue) => selectedValue.id === optionId)) {
			setFeedbackData((prevState) => ({
				...prevState,
				reason: { ...prevState.reason, value: prevState.reason.value.filter((value) => value.id !== optionId) },
			}));

			return;
		}
		setFeedbackData((prevState) => ({
			...prevState,
			reason: { id: fieldId, value: [...prevState.reason.value, { id: optionId, value, name: name }] },
		}));
	};

	const handleDescriptionInput = (value: string) => {
		if (feedbackData.reason.value.length === 0) {
			const field = fetchedForm?.fields.find((f) => getFieldType(f.type) === 'select');
			const option = field?.options?.find((o) => o.name === 'Annat.');

			if (option && field) {
				handleSelectInput(option.value, option.id, field.id, option.name);
			}
		}

		setFeedbackData((prevState) => ({ ...prevState, description: value }));
	};

	return (
		fetchedForm && (
			<div ref={formRef}>
				<FormValidationProvider>
					<CaptchaForm
						onSubmit={handleOnSubmit}
						onCancel={onCancel}
						formId="ZENDESK-education-feedback"
						name={`form_${fetchedForm.name}`}
						feedbackTitle={t`Tack för att du hjälper oss förbättra vårt innehåll!`}
						meta={{ type: 'zendesk' }}
					>
						<TextField
							hidden
							type="text"
							name="subject"
							value={`Feedback Education Video | On video with vimeoid: ${vimeoId}`}
						/>

						<div className={styles.fieldsContainer}>
							{getEditableFields(fetchedForm.fields).map((field, i) => {
								const fieldType = getFieldType(field.type);
								return (
									fieldType === 'select' && (
										<FieldValidationProvider key={i} id={`zendesk-form-${slugifyName(field.name)}`}>
											<div className={styles.checkboxContainer}>
												{!!field.options?.length &&
													field.options.map((option, i) => (
														<Checkbox
															key={i}
															label={option.name}
															bordered
															checked={feedbackData.reason.value.some(
																(selectedValue) => selectedValue.id === option.id,
															)}
															onClick={() => handleSelectInput(option.value, option.id, field.id, option.name)}
															value={option.value}
														/>
													))}
											</div>
										</FieldValidationProvider>
									)
								);
							})}

							{getEditableFields(fetchedForm.fields).map((field, i) => {
								return (
									field.type === 'description' && (
										<FieldValidationProvider key={i} id={`zendesk-form-${slugifyName(field.name)}`}>
											<Heading as="div" size="h6" title={t`Berätta gärna mer:`} marginBottom="none" />
											<TextField
												title={t`Ytterligare kommentarer`}
												name="description"
												testID={`zendesk-field-${slugifyName(field.name)}`}
												type="textarea"
												bordered
												onChange={(e) => handleDescriptionInput(e.target.value)}
												tooltip={t`Ytterligare kommentarer`}
												resize={false}
											/>
										</FieldValidationProvider>
									)
								);
							})}
						</div>
					</CaptchaForm>
				</FormValidationProvider>
			</div>
		)
	);
};
